<script setup lang="ts">
import InformationBaseCard from '@/components/Cards/InformationBaseCard.vue'
import { StackLayout } from '@/components/Layouts'
import { EDeviceStatus } from '@/types/enum/DeviceStatusEnum'
import type { ActionModel } from '@/types/models/ActionModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import moment from 'moment'
import { computed } from 'vue'
import store from '../../../../store'
import { EPropertyType } from '../../../../types/enum/PropertyTypeEnum'
import type { DeviceTemplateKeysModel } from '../../../../types/models/DeviceTemplateKeysModel'
import { EDevicesPerm } from '../../../../types/permissions/DevicesPermissionsEnum'
import { ERentalsPerm } from '../../../../types/permissions/RentalsPermissionsEnum'
import { EVersionsPerm } from '../../../../types/permissions/VersionsPermissionsEnum'
import GridLayout from '../../../Layouts/GridLayout.vue'
import PermissionWrapper from '../../../Permission/PermissionWrapper.vue'
import DeviceConfigurationItem from '../ConfigurationTab/ConfigurationComponents/DeviceConfigurationItem.vue'
import DeviceActionsCard from './DeviceActionsCard.vue'
import DeviceInformationCard from './DeviceInformationCard.vue'
import DeviceLocalization from './DeviceLocalization.vue'
import DeviceChangeSerialNumber from './components/DeviceChangeSerialNumber.vue'
import DeviceRentalsHistoryTable from './components/DeviceRentalsHistoryTable.vue'
import DeviceUpdateVersion from './components/DeviceUpdateVersion.vue'
import DeviceVersionsHistoryTable from './components/DeviceVersionsHistoryTable.vue'

type Props = {
    data: Partial<DeviceModel>
    status?: AlertSocketMessageModel
    templateKeys?: DeviceTemplateKeysModel[]
    actions?: ActionModel[]
}
const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'update', deviceValue: Partial<DeviceModel>): void
    (e: 'refresh'): void
}>()

const updateUserValue = (value: Partial<DeviceModel>) => emit('update', value)

const organization_id = computed(() => store.state.authentication.me.organization_id)

const currentlyRent = computed(
    () =>
        props.data?.rental?.start_at &&
        props.data?.rental?.end_at &&
        !!props.data?.rental?.owner_id &&
        props.data?.rental?.owner_id !== organization_id.value &&
        moment().isAfter(moment.utc(props.data?.rental.start_at)) &&
        moment().isBefore(moment.utc(props.data?.rental.end_at))
)

const configKeys = computed(
    () => props.templateKeys?.filter(item => item.property_type === EPropertyType.COMPONENT) ?? []
)
</script>

<template>
    <StackLayout direction="column" :gap="16" isResponsive>
        <StackLayout direction="row" :gap="16" isResponsive>
            <DeviceInformationCard
                :data="data"
                :status="status"
                @update="value => updateUserValue(value)">
                <slot name="informations" />
            </DeviceInformationCard>
            <InformationBaseCard
                v-if="configKeys?.length > 0"
                :label="$t('device.detail.informations.config')"
                class="w-full h-fit">
                <StackLayout :gap="16" direction="row" class="w-full">
                    <StackLayout :gap="16" direction="column" class="w-full">
                        <DeviceConfigurationItem
                            :data="data?.config"
                            :templateKeys="configKeys"
                            showAdvanced />
                    </StackLayout>
                </StackLayout>
            </InformationBaseCard>
            <InformationBaseCard
                :label="$t('device.detail.informations.actions')"
                class="full-width fit-height">
                <StackLayout
                    v-if="data.device_id || data.id"
                    :gap="16"
                    direction="column"
                    class="full-width">
                    <DeviceActionsCard v-if="actions?.length" :actions="actions" />
                    <slot name="actions" />
                    <GridLayout :gap="8" :maxItemPerRow="2" class="full-width">
                        <PermissionWrapper :permission="EDevicesPerm.DEVICES_FIRMWARE_UPDATE">
                            <DeviceUpdateVersion
                                :device="data"
                                @refresh="$emit('refresh')"
                                class="full-width" />
                        </PermissionWrapper>
                        <PermissionWrapper :permission="EDevicesPerm.DEVICES_EDIT_SERIAL">
                            <DeviceChangeSerialNumber :device="data" class="full-width" />
                        </PermissionWrapper>
                    </GridLayout>
                </StackLayout>
            </InformationBaseCard>
        </StackLayout>
        <PermissionWrapper v-if="!currentlyRent" :permission="ERentalsPerm.RENTALS_VIEW">
            <InformationBaseCard
                :label="$t('device.rentalsHistory.title')"
                class="full-width fit-height">
                <DeviceRentalsHistoryTable :deviceId="data?.device_id ?? data?.id" />
            </InformationBaseCard>
        </PermissionWrapper>
        <PermissionWrapper :permission="EVersionsPerm.VERSIONS_VIEW">
            <InformationBaseCard
                :label="$t('device.versionHistory.title')"
                class="full-width fit-height">
                <DeviceVersionsHistoryTable :deviceId="data?.device_id ?? data?.id" />
            </InformationBaseCard>
        </PermissionWrapper>
        <DeviceLocalization
            v-if="data.online === EDeviceStatus.ONLINE && data.gnss_location"
            :gnss_location="data.gnss_location" />
    </StackLayout>
</template>
